import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Activity, Clock, Filter, RefreshCw, CheckCircle2, XCircle, Send, ThumbsUp, ThumbsDown, Edit3 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { usePipelineFeed, useSkills, useCorrectIntent, useCurrentOrgId, type PipelineExecution } from "@/hooks/useSkillOS";

const INTENTS = ["question", "command", "complaint", "greeting", "task", "info", "analysis", "summary_request", "sales", "support", "poll", "welcome", "farewell", "escalation", "poll_request", "task_assign", "other"];

function qualityClass(q: number | null) {
  if (q == null) return "text-muted-foreground";
  if (q >= 75) return "text-emerald-500";
  if (q >= 50) return "text-amber-500";
  return "text-destructive";
}

export default function PipelineInspector() {
  const [skill, setSkill] = useState<string>("");
  const [intent, setIntent] = useState<string>("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<PipelineExecution | null>(null);

  const { data: skills = [] } = useSkills();
  const { data: feed = [], isLoading, refetch, isFetching } = usePipelineFeed({
    skill: skill || undefined,
    intent: intent || undefined,
    limit: 200,
  });

  const filtered = useMemo(() => {
    if (!search.trim()) return feed;
    const s = search.toLowerCase();
    return feed.filter((f) =>
      f.skill_name.toLowerCase().includes(s) ||
      (f.intent ?? "").toLowerCase().includes(s) ||
      (f.model_used ?? "").toLowerCase().includes(s)
    );
  }, [feed, search]);

  const stats = useMemo(() => {
    const total = filtered.length;
    const sent = filtered.filter((f) => f.was_sent_to_group).length;
    const avgMs = total ? filtered.reduce((a, f) => a + (f.execution_time_ms ?? 0), 0) / total : 0;
    const qScores = filtered.map((f) => f.quality_score).filter((q): q is number => typeof q === "number");
    const avgQ = qScores.length ? qScores.reduce((a, b) => a + b, 0) / qScores.length : 0;
    return { total, sent, avgMs: Math.round(avgMs), avgQ: Math.round(avgQ) };
  }, [filtered]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-headline">Pipeline Inspector</h1>
          <p className="text-muted-foreground text-sm">Telemetria em tempo real de cada execução do Skill OS.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`w-4 h-4 mr-1 ${isFetching ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Execuções" value={stats.total} icon={<Activity className="w-4 h-4" />} />
        <StatCard label="Enviadas" value={`${stats.sent}/${stats.total}`} icon={<Send className="w-4 h-4" />} />
        <StatCard label="Latência média" value={`${stats.avgMs}ms`} icon={<Clock className="w-4 h-4" />} />
        <StatCard label="Qualidade média" value={`${stats.avgQ}/100`} icon={<CheckCircle2 className="w-4 h-4" />} valueClass={qualityClass(stats.avgQ)} />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2"><Filter className="w-4 h-4" /> Filtros</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select value={skill || "all"} onValueChange={(v) => setSkill(v === "all" ? "" : v)}>
            <SelectTrigger><SelectValue placeholder="Todas as skills" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as skills</SelectItem>
              {skills.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={intent || "all"} onValueChange={(v) => setIntent(v === "all" ? "" : v)}>
            <SelectTrigger><SelectValue placeholder="Todas as intenções" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as intenções</SelectItem>
              {INTENTS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Buscar (skill, intent, modelo)…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Execuções recentes ({filtered.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">{[1,2,3,4,5].map((i) => <Skeleton key={i} className="h-14" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm">
              Nenhuma execução encontrada. Aguarde mensagens entrarem no pipeline.
            </div>
          ) : (
            <ScrollArea className="h-[520px]">
              <div className="space-y-1">
                {filtered.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setSelected(f)}
                    className="w-full text-left p-3 rounded-md hover:bg-muted/60 transition-colors border border-transparent hover:border-border"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <Badge variant="outline" className="text-xs">{f.skill_name}</Badge>
                        {f.intent && <Badge variant="secondary" className="text-xs">{f.intent}</Badge>}
                        {f.was_sent_to_group ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        )}
                        <span className="text-xs text-muted-foreground truncate">
                          {f.model_used ?? "—"}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs shrink-0">
                        <span className="text-muted-foreground">{f.execution_time_ms ?? 0}ms</span>
                        <span className={qualityClass(f.quality_score)}>{f.quality_score ?? "—"}</span>
                        <span className="text-muted-foreground">
                          {new Date(f.created_at).toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      <ExecutionDrawer execution={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function StatCard({ label, value, icon, valueClass }: { label: string; value: string | number; icon: React.ReactNode; valueClass?: string }) {
  return (
    <Card>
      <CardContent className="pt-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">{icon}{label}</div>
        <div className={`text-2xl font-bold font-headline ${valueClass ?? ""}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function ExecutionDrawer({ execution, onClose }: { execution: PipelineExecution | null; onClose: () => void }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: orgId } = useCurrentOrgId();
  const correctMut = useCorrectIntent();
  const [correctedIntent, setCorrectedIntent] = useState<string>("");
  const [editing, setEditing] = useState(false);

  const reactMut = useMutation({
    mutationFn: async ({ id, reaction }: { id: string; reaction: "positive" | "negative" }) => {
      const { error } = await supabase.from("skill_feedback").update({ admin_reaction: reaction }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["skill-os", "pipeline-feed"] });
      toast({ title: "Feedback registrado" });
    },
    onError: (e: Error) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const submitCorrection = () => {
    if (!execution || !execution.intent || !correctedIntent || !orgId) return;
    correctMut.mutate(
      { org_id: orgId, message_id: null, original_intent: execution.intent, corrected_intent: correctedIntent },
      {
        onSuccess: () => {
          toast({ title: "Correção registrada", description: `${execution.intent} → ${correctedIntent}` });
          setEditing(false);
          setCorrectedIntent("");
        },
        onError: (e) => toast({ title: "Erro", description: (e as Error).message, variant: "destructive" }),
      },
    );
  };

  return (
    <Dialog open={!!execution} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Execução do Pipeline</DialogTitle>
        </DialogHeader>
        {execution && (
          <ScrollArea className="max-h-[70vh]">
            <div className="space-y-4 pr-3">
              <Section title="Identificação">
                <Row label="ID" value={execution.id} mono />
                <Row label="Quando" value={new Date(execution.created_at).toLocaleString()} />
                <Row label="Skill" value={execution.skill_name} />
                <Row label="Intent" value={`${execution.intent ?? "—"} (${execution.intent_confidence != null ? Math.round(execution.intent_confidence * 100) + "%" : "—"})`} />
              </Section>

              <Section title="Ações do operador">
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant={execution.admin_reaction === "positive" ? "default" : "outline"}
                    onClick={() => reactMut.mutate({ id: execution.id, reaction: "positive" })}
                    disabled={reactMut.isPending}
                  >
                    <ThumbsUp className="w-3 h-3 mr-1" /> Boa
                  </Button>
                  <Button
                    size="sm"
                    variant={execution.admin_reaction === "negative" ? "destructive" : "outline"}
                    onClick={() => reactMut.mutate({ id: execution.id, reaction: "negative" })}
                    disabled={reactMut.isPending}
                  >
                    <ThumbsDown className="w-3 h-3 mr-1" /> Ruim
                  </Button>
                  {execution.intent && !editing && (
                    <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                      <Edit3 className="w-3 h-3 mr-1" /> Corrigir intent
                    </Button>
                  )}
                  {editing && execution.intent && (
                    <>
                      <Select value={correctedIntent} onValueChange={setCorrectedIntent}>
                        <SelectTrigger className="w-44 h-8 text-xs"><SelectValue placeholder="Intent correto" /></SelectTrigger>
                        <SelectContent>
                          {INTENTS.filter((i) => i !== execution.intent).map((i) => (
                            <SelectItem key={i} value={i}>{i}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button size="sm" onClick={submitCorrection} disabled={!correctedIntent || correctMut.isPending}>Enviar</Button>
                      <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setCorrectedIntent(""); }}>Cancelar</Button>
                    </>
                  )}
                </div>
              </Section>

              <Section title="Execução">
                <Row label="Modelo" value={execution.model_used ?? "—"} />
                <Row label="Latência" value={`${execution.execution_time_ms ?? 0}ms`} />
                <Row label="Output (chars)" value={execution.output_length ?? 0} />
                <Row label="Memórias usadas" value={execution.memory_used_count ?? 0} />
                <Row label="Variante A/B" value={execution.ab_variant ?? "—"} />
              </Section>

              <Section title="Resultado">
                <Row label="Qualidade" value={execution.quality_score != null ? `${execution.quality_score}/100` : "—"} />
                <Row label="Enviado ao grupo" value={execution.was_sent_to_group ? "Sim" : "Não"} />
                <Row label="Follow-ups" value={execution.follow_up_messages ?? 0} />
                <Row label="Reação admin" value={execution.admin_reaction ?? "—"} />
              </Section>

              <Section title="Contexto">
                <Row label="Group ID" value={execution.group_id ?? "—"} mono />
                <Row label="Member ID" value={execution.member_id ?? "—"} mono />
                <Row label="Decision ID" value={execution.decision_id ?? "—"} mono />
              </Section>

              {execution.context_metadata != null && (
                <Section title="Metadata">
                  <pre className="text-xs bg-muted p-3 rounded-md overflow-auto max-h-60">
                    {JSON.stringify(execution.context_metadata, null, 2)}
                  </pre>
                </Section>
              )}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string | number; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className={`text-right break-all ${mono ? "font-mono text-xs" : ""}`}>{value}</span>
    </div>
  );
}
